import { Capacitor } from "@capacitor/core";
import { useEffect, useId, useState } from "react";
import "./pwa-install.css";

function isStandalone() {
  return Boolean(navigator.standalone
    || window.matchMedia?.("(display-mode: standalone)")?.matches);
}

function isIosSafari() {
  const ios = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios && /Safari/.test(navigator.userAgent)
    && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
}

export function PwaInstallHint() {
  const titleId = useId();
  const stepsId = useId();
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [installFailed, setInstallFailed] = useState(false);
  const native = Capacitor.isNativePlatform();
  const safari = isIosSafari();

  useEffect(() => {
    if (native || installed) return undefined;
    function onInstallAvailable(event) {
      event.preventDefault();
      setInstallEvent(event);
      setInstallFailed(false);
    }
    function onInstalled() { setInstalled(true); }
    const displayMode = window.matchMedia?.("(display-mode: standalone)");
    function onDisplayModeChanged() { setInstalled(isStandalone()); }
    window.addEventListener("beforeinstallprompt", onInstallAvailable);
    window.addEventListener("appinstalled", onInstalled);
    displayMode?.addEventListener?.("change", onDisplayModeChanged);
    return () => {
      window.removeEventListener("beforeinstallprompt", onInstallAvailable);
      window.removeEventListener("appinstalled", onInstalled);
      displayMode?.removeEventListener?.("change", onDisplayModeChanged);
    };
  }, [native, installed]);

  async function install() {
    if (safari) {
      setExpanded((value) => !value);
      return;
    }
    if (!installEvent) return;
    const prompt = installEvent;
    setInstallEvent(null);
    try {
      await prompt.prompt();
      if ((await prompt.userChoice)?.outcome === "accepted") setInstalled(true);
    } catch {
      setInstallFailed(true);
    }
  }

  if (native || installed || dismissed || (!safari && !installEvent && !installFailed)) {
    return null;
  }

  return (
    <aside className="pwa-install" aria-labelledby={titleId}>
      <img className="pwa-install__icon" src="/app-icon-192.png" width="44" height="44" alt="" />
      <div className="pwa-install__body">
        <h2 id={titleId}>AxonLeads на главном экране</h2>
        <p>Открывайте кабинет одним касанием.</p>
        {installFailed ? (
          <p role="status">Откройте меню браузера и выберите «Установить приложение».</p>
        ) : (
          <button
            className="pwa-install__action"
            type="button"
            aria-expanded={safari ? expanded : undefined}
            aria-controls={safari && expanded ? stepsId : undefined}
            onClick={install}
          >
            {safari ? "Добавить на главный экран" : "Установить приложение"}
          </button>
        )}
        {safari && expanded ? (
          <ol id={stepsId} className="pwa-install__steps">
            <li>Нажмите «Поделиться» в Safari — квадрат со стрелкой вверх.</li>
            <li>Выберите «На экран Домой» в списке действий.</li>
            <li>Проверьте иконку AxonLeads и нажмите «Добавить».</li>
          </ol>
        ) : null}
      </div>
      <button
        className="pwa-install__dismiss"
        type="button"
        aria-label="Скрыть предложение установки"
        onClick={() => setDismissed(true)}
      >×</button>
    </aside>
  );
}
